import { inject, Injectable, OnDestroy } from '@angular/core';
import { SharedLanguageService } from '@language';
import {
    ActionItem,
    ActionItemStyle,
    ApiFragment,
    CustomErrorType,
    Mh2Entity,
    NavigationLink,
    NotificationType,
    PartialToast,
    PerformedAction,
    ShToastFactory,
    Toast
} from '@stylehaven/components';
import { isArray } from 'lodash-es';
import { BehaviorSubject, combineLatest, EMPTY, Observable, of, Subject } from 'rxjs';
import { catchError, finalize, map, take, takeUntil } from 'rxjs/operators';
import { ActionItemOptions } from '../common/entities/common/action-item-function.entity';
import { BatchUserAction } from '../common/entities/common/batch-user-action.entity';
import { BulkUserAction } from '../common/entities/common/bulk-user-action.entity';
import { UserAction } from '../common/entities/common/user-action.entity';
import { StoreService } from '../common/services/store/store.service';

type ToastSubject = Mh2Entity | string | undefined;

@Injectable({ providedIn: 'root' })
export abstract class GenericActionService<
    T extends object,
    S extends string,
    U extends UserAction | BulkUserAction | BatchUserAction = UserAction
> implements OnDestroy {
    protected abstract readonly action: S;

    // Services
    protected readonly translateService: SharedLanguageService = inject(SharedLanguageService);
    protected readonly storeService: StoreService = inject(StoreService);
    protected readonly toastFactory: ShToastFactory = inject(ShToastFactory);

    // Observables
    private readonly destroy$: Subject<void> = new Subject<void>();
    public readonly performedAction$: Subject<PerformedAction<ApiFragment>> = new Subject();
    public readonly actionsExecuting: Map<string, BehaviorSubject<boolean>> = new Map<
        string,
        BehaviorSubject<boolean>
    >();

    // Toast
    protected toastId: number | undefined = undefined;

    // Behaviour
    protected readonly ENABLE_BUSY_TOAST: boolean = true;
    protected readonly ENABLE_FINISHED_TOASTS: boolean = true;
    protected abstract readonly REFRESH_ON_ACTION: boolean;
    protected abstract readonly DISABLE_ON_EXECUTE: boolean;
    protected readonly BUTTON_STYLE: ActionItemStyle = ActionItemStyle.Primary;
    protected readonly REFRESH_ON_FAILURE: boolean = false;

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    // #region Action
    public getActionItem = (
        target: T,
        action: S,
        actionItemOptions: ActionItemOptions
    ): Observable<ActionItem | null> => {
        // MED-6483: Ensures any failure does not block other actions
        try {
            return combineLatest([this.getUserAction(target, actionItemOptions), this.getActionExecuting(target)]).pipe(
                map(([userAction, executing]) => {
                    if (!userAction || !userAction.isAvailable()) {
                        return null;
                    }

                    let disabled: boolean;
                    let disabledInfoMsg: string;
                    if (this.DISABLE_ON_EXECUTE && executing) {
                        disabled = true;
                        disabledInfoMsg = this.translateService.translate(
                            'action-tooltips.general.action-executing.generic'
                        );
                    } else {
                        disabled = userAction.isDisabled();
                        disabledInfoMsg = this.getDisabledMessage(target, userAction);
                    }

                    const icon: string | undefined = this.getIcon(target, actionItemOptions);
                    const style: ActionItemStyle = icon ? ActionItemStyle.PureIconButton : this.BUTTON_STYLE;

                    return {
                        label: this.translateService.translate(this.getLabelKey(target)),
                        eventListener: this.performAction.bind(this, target, actionItemOptions),
                        disabled,
                        disabledInfoMsg,
                        icon,
                        style
                    };
                }),
                catchError(error => {
                    console.error(`Something went wrong during the evaluation of ${this.action}: ${error}`);
                    return of(null);
                })
            );
        } catch (error) {
            console.error(`Something went wrong during the loading of ${this.action}: ${error}`);
            return of(null);
        }
    };

    public abstract getUserAction(target: T, actionItemOptions: ActionItemOptions): Observable<U>;

    /**
     * Default order of toasts and action
     * Could be overridden to implement different behaviour
     *
     * @param target
     * @param actionItemOptions
     */
    protected performAction(target: T, actionItemOptions: ActionItemOptions): void {
        this.initBusyToast(target, actionItemOptions);
        this.setActionExecutionStart(target);

        this.onAction(target, actionItemOptions)
            .pipe(
                map(success => this.afterAction(success, target, actionItemOptions)),
                catchError(error => this.onError(error, target, actionItemOptions)),
                finalize(() => this.setActionExecutionEnd(target)), // Some actions close the stream before a success or error (e.g., cancel event). Make sure set processing to false in this case
                take(1),
                takeUntil(this.destroy$)
            )
            .subscribe();
    }

    /**
     * Sets busy toast if enabled
     * @param target The target of the action
     * @param actionItemOptions The options of the action item
     * @param delayed If the toast is started outside of normal action execution. (e.g. if it needs to wait for a dialog)
     */
    protected initBusyToast(target: T, actionItemOptions: ActionItemOptions, delayed: boolean = false): void {
        if (!this.ENABLE_BUSY_TOAST && !delayed) {
            return;
        }

        const actionBusyToast: Toast = this.createActionBusyToast({
            messageRoot: this.getToastLabelKey(target),
            entityType: this.getToastEntity(target, actionItemOptions),
            totalFragments: isArray(target) ? target.length : 1,
            translationOptions: this.getToastTranslationOptions(target, actionItemOptions)
        });

        this.toastId = actionBusyToast.id;
        this.addToast(actionBusyToast);
    }

    /**
     * Action implementation
     * Should return an observable boolean if the action was successful or not
     *
     * @param target
     * @param actionItemOptions
     */
    protected abstract onAction(target: T, actionItemOptions: ActionItemOptions): Observable<boolean>;

    protected onError(error: Error, target: T, actionItemOptions: ActionItemOptions): Observable<boolean> {
        console.error(`${this.action}:: ${error.message ?? error}`);

        const success = false;
        this.afterAction(success, target, actionItemOptions, error);
        return EMPTY;
    }

    /**
     * Stuff to do after triggering the action
     * Sets completion toast by default and emits performed action
     * Override this function to implement other behaviour
     *
     * @param successful
     * @param target
     * @param actionItemOptions
     * @param error
     */
    protected afterAction(successful: boolean, target: T, actionItemOptions: ActionItemOptions, error?: Error): void {
        if (this.toastId) {
            this.closeToast(this.toastId);
        } else {
            console.warn('No toast id found. Make sure to call initBusyToast before calling afterAction');
        }

        this.setActionExecutionEnd(target);

        this.handleAfterActionToast(successful, target, actionItemOptions, error);

        this.performedAction$.next({
            action: this.action,
            target: isArray(target) ? target[0] : target,
            successful,
            warrantsRefresh: this.REFRESH_ON_ACTION && (successful || this.REFRESH_ON_FAILURE)
        });
    }

    private handleAfterActionToast(
        successful: boolean,
        target: T,
        actionItemOptions: ActionItemOptions,
        error?: Error
    ) {
        if (!this.ENABLE_FINISHED_TOASTS) {
            return;
        }

        const labelKey: string = this.getToastLabelKey(target);
        const entity: string | undefined = this.getToastEntity(target, actionItemOptions);
        const translationOptions = this.getToastTranslationOptions(target, actionItemOptions);

        if (!successful) {
            const handledCustomError = error ? this.handleCustomErrors(error, target) : false;

            if (!handledCustomError) {
                this.createAndAddActionErrorToast({
                    messageRoot: labelKey,
                    entityType: entity,
                    totalFragments: isArray(target) ? target.length : 1,
                    translationOptions
                });
            }

            return;
        }

        const successLink: NavigationLink | undefined = this.getSuccessToastLink(target, actionItemOptions);
        const linkText: string | undefined = successLink
            ? this.getSuccessToastLinkLabelKey(target, actionItemOptions)
            : undefined;

        this.createAndAddActionSuccessToast({
            messageRoot: labelKey,
            entityType: entity,
            linkText: linkText,
            totalFragments: isArray(target) ? target.length : 1,
            route: successLink ? successLink.route : undefined,
            translationOptions
        });
    }

    // Throw warning toast on Index Timeout
    // Target is used in overrides
    protected handleCustomErrors(error: CustomErrorType | Error, target: T): boolean {
        if (error === CustomErrorType.IndexTimeout) {
            this.addToast(
                this.toastFactory.create({
                    type: NotificationType.Warning,
                    message: this.translate('errors.index-timeout')
                })
            );
            return true;
        }

        return false;
    }
    // #endregion

    // #region Action Label
    public abstract getLabelKey(target: T): string;

    protected abstract getDisabledMessage(target: T, userAction: U): string;
    // #endregion

    // #region Action Styling
    protected getIcon(target: T, actionItemOptions: ActionItemOptions): string | undefined {
        return undefined;
    }
    // #endregion

    // #region Toast Config
    public abstract getToastLabelKey(target: T): string;

    public abstract getToastEntity(target: T, actionItemOptions: ActionItemOptions): ToastSubject;

    public abstract getSuccessToastLinkLabelKey(target: T, actionItemOptions: ActionItemOptions): string;

    public abstract getSuccessToastLink(target: T, actionItemOptions: ActionItemOptions): NavigationLink | undefined;

    public abstract getToastTranslationOptions(
        target: T,
        actionItemOptions: ActionItemOptions
    ): Record<string, string | number>;
    // #endregion

    // #region Executing
    public abstract getActionExecuting(target: T): Observable<boolean>;

    public abstract setActionExecutionStart(target: T): void;

    public abstract setActionExecutionEnd(target: T): void;
    // #endregion

    // #region Toasts Utils
    public addToast(toast: Toast): void {
        this.storeService.addNewToast(toast);
    }

    public closeToast(toastId: number): void {
        this.storeService.deleteToast(toastId);
    }

    public updateToast(toastId: number, toast: Partial<Toast>): void {
        const updatedToast: Toast = this.toastFactory.create({ ...toast, id: toastId });
        this.storeService.addToast(updatedToast);
    }

    public createAndAddNewToast(
        messageToken: string,
        type: NotificationType,
        linkTextToken?: string,
        route?: string
    ): Toast {
        const toast: Toast = this.toastFactory.create({
            message: this.translateService.translate(messageToken),
            type,
            ...(route &&
                linkTextToken && {
                    navigationLink: { route },
                    linkText: this.translateService.translate(linkTextToken)
                })
        });

        this.addToast(toast);
        return toast;
    }

    public createAndAddActionSuccessToast(toastConfig: {
        messageRoot: string;
        entityType: ToastSubject;
        totalFragments?: number;
        extraMessages?: string[];
        linkText?: string;
        route?: string;
        translationOptions?: Record<string, string | number>;
    }): Toast {
        const partialToast: PartialToast = {
            type: NotificationType.Success
        };

        const joinedTranslationOptions: { [key: string]: string | number } = {
            ...toastConfig.translationOptions,
            amount: toastConfig.totalFragments ?? 1
        };

        const toast: Toast = this.createActionToast(partialToast, {
            messageRoot: toastConfig.messageRoot,
            actionType: 'success',
            entityType: toastConfig.entityType,
            extraMessages: toastConfig.extraMessages,
            linkText: toastConfig.linkText,
            route: toastConfig.route,
            translationOptions: joinedTranslationOptions
        });

        this.addToast(toast);
        return toast;
    }

    public createAndAddActionWarningToast(toastConfig: {
        messageRoot: string;
        entityType: ToastSubject;
        totalFragments?: number;
        extraMessages?: string[];
        linkText?: string;
        route?: string;
    }): Toast {
        const partialToast: PartialToast = {
            type: NotificationType.Warning
        };

        const toast: Toast = this.createActionToast(partialToast, {
            messageRoot: toastConfig.messageRoot,
            actionType: 'warning',
            entityType: toastConfig.entityType,
            extraMessages: toastConfig.extraMessages,
            linkText: toastConfig.linkText,
            route: toastConfig.route,
            translationOptions: { amount: toastConfig.totalFragments ?? 1 }
        });

        this.addToast(toast);
        return toast;
    }

    public createAndAddActionErrorToast(toastConfig: {
        messageRoot: string;
        entityType: ToastSubject;
        totalFragments?: number;
        extraMessages?: string[];
        linkText?: string;
        route?: string;
        translationOptions?: Record<string, string | number>;
    }): Toast {
        const partialToast: PartialToast = {
            type: NotificationType.Error
        };

        const joinedTranslationOptions: { [key: string]: string | number } = {
            ...toastConfig.translationOptions,
            amount: toastConfig.totalFragments ?? 1
        };

        const toast: Toast = this.createActionToast(partialToast, {
            messageRoot: toastConfig.messageRoot,
            actionType: 'error',
            entityType: toastConfig.entityType,
            extraMessages: toastConfig.extraMessages,
            linkText: toastConfig.linkText,
            route: toastConfig.route,
            translationOptions: joinedTranslationOptions
        });

        this.addToast(toast);
        return toast;
    }

    public createActionBusyToast(toastConfig: {
        messageRoot: string;
        entityType: ToastSubject;
        totalFragments?: number;
        extraMessages?: string[];
        linkText?: string;
        route?: string;
        translationOptions?: Record<string, string | number>;
    }): Toast {
        const partialToast: PartialToast = {
            type: NotificationType.Update,
            autoClose: false,
            iconRotate: true,
            icon: 'spinner'
        };

        const joinedTranslationOptions: { [key: string]: string | number } = {
            ...toastConfig.translationOptions,
            amount: toastConfig.totalFragments ?? 1
        };

        const toast: Toast = this.createActionToast(partialToast, {
            messageRoot: toastConfig.messageRoot,
            actionType: 'busy',
            entityType: toastConfig.entityType,
            extraMessages: toastConfig.extraMessages,
            linkText: toastConfig.linkText,
            route: toastConfig.route,
            translationOptions: joinedTranslationOptions
        });

        return toast;
    }

    private createActionToast(
        partialToast: PartialToast,
        toastConfig: {
            messageRoot: string;
            actionType: string;
            entityType: ToastSubject;
            translationOptions: { [key: string]: string | number };
            extraMessages: string[] | undefined;
            linkText: string | undefined;
            route: string | undefined;
        }
    ): Toast {
        const key: string = toastConfig.entityType
            ? `${toastConfig.messageRoot}.${toastConfig.actionType}.${toastConfig.entityType}`
            : `${toastConfig.messageRoot}.${toastConfig.actionType}`;
        let toastMessages: string[] = [this.translate(key, toastConfig.translationOptions)];

        if (toastConfig.extraMessages) {
            toastMessages = [...toastMessages, ...toastConfig.extraMessages];
        }

        const actionToast: PartialToast = {
            ...partialToast,
            message: toastMessages.join(' '),
            ...(toastConfig.route &&
                toastConfig.linkText && {
                    navigationLink: { route: toastConfig.route },
                    linkText: this.translate(toastConfig.linkText, toastConfig.translationOptions)
                })
        };

        return this.toastFactory.create(actionToast);
    }
    // #endregion

    // #region Utils
    protected translate(token: string, params?: object | undefined) {
        return this.translateService.instant(token, params);
    }
    // #endregion
}
